import { useCallback, useEffect, useState } from 'react';
import type { PageTourStep } from './pageTourTypes';

const TOUR_OPEN_ATTR = 'data-page-tour-open';
const TOUR_STYLE_ID = 'page-tour-hotkey-hide-style';

export type Rect = { top: number; left: number; width: number; height: number };

export function findTourTarget(targetId: string): HTMLElement | null {
  return document.querySelector(`[data-tour-id="${CSS.escape(targetId)}"]`);
}

/** Área visible de contenido: debajo del AppBar fijo y dentro de `.app-main`. */
export function contentViewport(): Rect {
  const main = document.querySelector<HTMLElement>('.app-main')?.getBoundingClientRect();
  const header = document.querySelector<HTMLElement>('header.MuiAppBar-root')?.getBoundingClientRect();
  const top = Math.max(0, header?.bottom ?? 0, main?.top ?? 0);
  const left = Math.max(0, main?.left ?? 0);
  const right = Math.min(window.innerWidth, main?.right ?? window.innerWidth);
  const bottom = Math.min(window.innerHeight, main?.bottom ?? window.innerHeight);
  return { top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

const SCROLLS = /(auto|scroll|overlay)/;

function scrollableAncestors(el: HTMLElement): HTMLElement[] {
  const list: HTMLElement[] = [];
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    if (SCROLLS.test(getComputedStyle(p).overflowY) && p.scrollHeight > p.clientHeight + 1) {
      list.push(p);
    }
  }
  return list;
}

/** Rectángulo del elemento recortado por los contenedores con overflow (p. ej. tablas con scroll horizontal). */
export function visibleRect(el: HTMLElement): DOMRect {
  const r = el.getBoundingClientRect();
  let { top, left, right, bottom } = r;
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const s = getComputedStyle(p);
    if (s.overflowX === 'visible' && s.overflowY === 'visible') continue;
    const pr = p.getBoundingClientRect();
    if (s.overflowX !== 'visible') {
      left = Math.max(left, pr.left);
      right = Math.min(right, pr.right);
    }
    if (s.overflowY !== 'visible' && !SCROLLS.test(s.overflowY)) {
      top = Math.max(top, pr.top);
      bottom = Math.min(bottom, pr.bottom);
    }
  }
  return new DOMRect(left, top, Math.max(0, right - left), Math.max(0, bottom - top));
}

/** Desplaza horizontalmente los contenedores (tablas anchas) para que el campo quede a la vista. */
function scrollHorizontallyIntoView(el: HTMLElement) {
  const margin = 12;
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    if (!SCROLLS.test(getComputedStyle(p).overflowX) || p.scrollWidth <= p.clientWidth + 1) continue;
    const r = el.getBoundingClientRect();
    const pr = p.getBoundingClientRect();
    if (r.right > pr.right) p.scrollLeft += r.right - pr.right + margin;
    else if (r.left < pr.left) p.scrollLeft -= pr.left - r.left + margin;
  }
}

/** Centra el campo en el área de contenido (o alinea arriba si es más alto que ella). */
export function scrollTargetIntoView(el: HTMLElement) {
  scrollHorizontallyIntoView(el);
  const vp = contentViewport();
  const r = el.getBoundingClientRect();
  const margin = 16;
  const idealTop = r.height + margin * 2 >= vp.height ? vp.top + margin : vp.top + (vp.height - r.height) / 2;
  let delta = r.top - idealTop;
  for (const p of scrollableAncestors(el)) {
    const before = p.scrollTop;
    p.scrollTop += delta;
    delta -= p.scrollTop - before;
    if (Math.abs(delta) < 1) return;
  }
  if (Math.abs(delta) >= 1) window.scrollBy(0, delta);
}

function ensureHotkeyHideStyle() {
  if (document.getElementById(TOUR_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = TOUR_STYLE_ID;
  style.textContent = `body[${TOUR_OPEN_ATTR}="true"] [data-form-hotkey-bar="true"] { visibility: hidden !important; }`;
  document.head.appendChild(style);
}

export type UsePageTourOptions = {
  steps: PageTourStep[];
  onComplete?: () => void;
};

export function usePageTour({ steps, onComplete }: UsePageTourOptions) {
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const available = steps.map((s) => (open ? Boolean(findTourTarget(s.targetId)) : true));
  const findFrom = (from: number, dir: 1 | -1) => {
    for (let i = from; i >= 0 && i < steps.length; i += dir) {
      if (findTourTarget(steps[i].targetId)) return i;
    }
    return -1;
  };

  const start = useCallback(() => {
    const idx = steps.findIndex((s) => findTourTarget(s.targetId));
    if (idx < 0) return;
    setStepIndex(idx);
    setOpen(true);
  }, [steps]);

  const close = useCallback(() => setOpen(false), []);

  const next = () => {
    const idx = findFrom(stepIndex + 1, 1);
    if (idx < 0) {
      setOpen(false);
      onComplete?.();
    } else {
      setStepIndex(idx);
    }
  };

  const prev = () => {
    const idx = findFrom(stepIndex - 1, -1);
    if (idx >= 0) setStepIndex(idx);
  };

  const currentStep = open ? steps[stepIndex] ?? null : null;

  useEffect(() => {
    if (!open) return undefined;
    ensureHotkeyHideStyle();
    document.body.setAttribute(TOUR_OPEN_ATTR, 'true');
    return () => document.body.removeAttribute(TOUR_OPEN_ATTR);
  }, [open]);

  useEffect(() => {
    if (!currentStep) return undefined;
    const raf = window.requestAnimationFrame(() => {
      const el = findTourTarget(currentStep.targetId);
      if (el) scrollTargetIntoView(el);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [currentStep]);

  return {
    open,
    start,
    close,
    next,
    prev,
    currentStep,
    isFirst: findFrom(stepIndex - 1, -1) < 0,
    isLast: findFrom(stepIndex + 1, 1) < 0,
    stepOrdinal: available.slice(0, stepIndex + 1).filter(Boolean).length || 1,
    stepCount: available.filter(Boolean).length,
  };
}
